import React, { useState } from "react";
import FadeInSection from "./FadeInSection";
import SliderInput from "./SliderInput";

interface PackageDef {
    key: string;
    price: number;
    card: (isBestValue: boolean) => JSX.Element;
}

const Pricing = () => {

    const coreMinPlayers = 50;
    const coreBasePrice = 14.99;
    const coreAdditionalCostPerPlayer = 0.50;

    const essentialMinPlayers = 75;
    const essentialBasePrice = 24.99;
    const essentialAdditionalCostPerPlayer = 0.35;

    const ultimateMinPlayers = 100;
    const ultimateBasePrice = 34.99;
    const ultimateAdditionalCostPerPlayer = 0.10;

    const [playerCount, setPlayerCount] = useState(coreMinPlayers);

    const coreTotalPrice = coreBasePrice + Math.max(0, (playerCount - coreMinPlayers) * coreAdditionalCostPerPlayer);
    const essentialTotalPrice = essentialBasePrice + Math.max(0, (playerCount - essentialMinPlayers) * essentialAdditionalCostPerPlayer);
    const ultimateTotalPrice = ultimateBasePrice + Math.max(0, (playerCount - ultimateMinPlayers) * ultimateAdditionalCostPerPlayer);

    const minPrice = Math.min(coreTotalPrice, essentialTotalPrice, ultimateTotalPrice);

    const BestValueBadge = () => (
        <div className="best-value-badge">
            <span className="material-symbols-outlined">verified</span>
            Best Value for {playerCount} Players
        </div>
    );

    const packages: PackageDef[] = [
        {
            key: 'core',
            price: coreTotalPrice,
            card: (isBestValue) => (
                <div className={`card core${isBestValue ? ' best-value' : ''}`}>
                    {isBestValue && <BestValueBadge />}
                    <h3><span className="material-symbols-outlined">trophy</span> Core</h3>
                    <hr/>
                    <h5>From <b>£{coreTotalPrice.toFixed(2)}</b> Per Month</h5>
                    <ul>
                        <li><span className="material-symbols-outlined">task_alt</span> Base - <b>£14.99/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Players - Up to <b>50 Included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Player Addons - <b>£0.50ppp/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Coaches Corner</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Parents Corner</li>
                        <li><span className="material-symbols-outlined">task_alt</span> BootXchange</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Fixture Sanctioning</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Stripe Integration</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Contact Us</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Users - Up to <b>150 included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Documents - Up to <b>150mb included</b></li>
                        <li><span className="material-symbols-outlined">cancel</span> Documents Extra Storage</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Ticket Based Support</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Team Bio</li>
                        <li><span className="material-symbols-outlined">task_alt</span> News</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Fixtures</li>
                        <li><span className="material-symbols-outlined">cancel</span> Expense Management</li>
                        <li><span className="material-symbols-outlined">cancel</span> Custom Domain Name</li>
                        <li><span className="material-symbols-outlined">cancel</span> Welfare Case Management</li>
                    </ul>
                    <a href="https://checkout.grassroots360.co.uk/?package=Core" className="signup-btn" target="_blank" rel="noreferrer">Sign Up Now</a>
                </div>
            )
        },
        {
            key: 'essential',
            price: essentialTotalPrice,
            card: (isBestValue) => (
                <div className={`card essential${isBestValue ? ' best-value' : ''}`}>
                    {isBestValue && <BestValueBadge />}
                    <h3><span className="material-symbols-outlined">rewarded_ads</span>Essential</h3>
                    <hr/>
                    <h5>From <b>£{essentialTotalPrice.toFixed(2)}</b> Per Month</h5>
                    <ul>
                        <li><span className="material-symbols-outlined">task_alt</span> Base - <b>£24.99/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Players - Up to <b>75 Included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Player Addons - <b>£0.35ppp/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Coaches Corner</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Parents Corner</li>
                        <li><span className="material-symbols-outlined">task_alt</span> BootXchange</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Fixture Sanctioning</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Stripe Integration</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Contact Us</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Users - Up to <b>2500 included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Documents - Up to <b>1Gb included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Documents Extra Storage - <b>50p/Gb</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Ticket Based Support</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Team Bio</li>
                        <li><span className="material-symbols-outlined">task_alt</span> News</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Fixtures</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Expense Management</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Custom Domain Name - <b>£4.99/m</b></li>
                        <li><span className="material-symbols-outlined">cancel</span> Welfare Case Management</li>
                    </ul>
                    <a href="https://checkout.grassroots360.co.uk/?package=Essential" className="signup-btn" target="_blank" rel="noreferrer">Sign Up Now</a>
                </div>
            )
        },
        {
            key: 'ultimate',
            price: ultimateTotalPrice,
            card: (isBestValue) => (
                <div className={`card ultimate${isBestValue ? ' best-value' : ''}`}>
                    {isBestValue && <BestValueBadge />}
                    <h3><span className="material-symbols-outlined">editor_choice</span>Ultimate</h3>
                    <hr/>
                    <h5>From <b>£{ultimateTotalPrice.toFixed(2)}</b> Per Month</h5>
                    <ul>
                        <li><span className="material-symbols-outlined">task_alt</span> Base - <b>£34.99/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Players - Up to <b>100 Included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Player Addons - <b>£0.10ppp/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Coaches Corner</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Parents Corner</li>
                        <li><span className="material-symbols-outlined">task_alt</span> BootXchange</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Fixture Sanctioning</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Stripe Integration</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Contact Us</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Users - Up to <b>7500 included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Documents - Up to <b>2Gb included</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Documents Extra Storage - <b>25p/Gb</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Ticket Based Support</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Team Bio</li>
                        <li><span className="material-symbols-outlined">task_alt</span> News</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Fixtures</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Expense Management</li>
                        <li><span className="material-symbols-outlined">task_alt</span> Custom Domain Name - <b>£2.99/m</b></li>
                        <li><span className="material-symbols-outlined">task_alt</span> Welfare Case Management</li>
                    </ul>
                    <a href="https://checkout.grassroots360.co.uk/?package=Ultimate" className="signup-btn" target="_blank" rel="noreferrer">Sign Up Now</a>
                </div>
            )
        }
    ];

    const sortedPackages = [...packages].sort((a, b) => a.price - b.price);

    return(
        <FadeInSection>
            <section className="row gap-2" id="pricing">
                <div className="container">
                    <div className="col-12-xs pl-4 pr-4">
                        <div className="card pricing">
                            <h2>Pricing...</h2>
                            <h4>Need a flexible pricing plan? No problems, just provide us with a rough estimate of what you need and check out the plans below...</h4>
                            <SliderInput value={playerCount} setValue={setPlayerCount} />
                        </div>
                    </div>
                </div>
                <div className="container pricing-cards">
                    {sortedPackages.map(pkg => (
                        <div key={pkg.key} className="col-12-xs col-4-md">
                            {pkg.card(pkg.price === minPrice)}
                        </div>
                    ))}
                </div>
            </section>
        </FadeInSection>
    );
}

export default Pricing;
